// src/components/chat/PaymentRequestBubble.js
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as offersService from "../../services/offersService";
import * as chatService from "../../services/chatService";
import DealRatingModal from "./DealRatingModal";
import { errorDev } from '../../utils/logger.js';

export default function PaymentRequestBubble({ msg, currentUser, isSender, align = "left", onPaymentSuccess, chatId }) {
  const { t } = useTranslation(["inbox", "common"]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [showRate, setShowRate] = useState(false);
  const [ratePrompt, setRatePrompt] = useState("");
  const [offerDoc, setOfferDoc] = useState(null);

  const [dealStatus, setDealStatus] = useState(String(msg?.extra?.dealStatus || "accepted").toLowerCase());
  const [sentConfirmed, setSentConfirmed] = useState(Boolean(msg?.extra?.deliverySentAt));
  const [receivedConfirmed, setReceivedConfirmed] = useState(Boolean(msg?.extra?.deliveryReceivedAt));
  const completed = dealStatus === "completed" || (sentConfirmed && receivedConfirmed);

  const amount = msg?.extra?.amount ?? offerDoc?.amount;
  const productTitle = msg?.extra?.productTitle || offerDoc?.productTitle || "";
  const offerId = msg?.extra?.offerId;
  const buyerId = msg?.extra?.buyerId || offerDoc?.buyerId || null;
  const sellerId = msg?.extra?.sellerId || offerDoc?.sellerId || null;
  const buyerName = msg?.extra?.buyerName || null;
  const sellerName = msg?.extra?.sellerName || null;
  const isBuyer = String(currentUser?.uid || "") === String(buyerId || "");
  const isSeller = String(currentUser?.uid || "") === String(sellerId || "");
  const ratingTargetLabel = isSeller ? (buyerName || "") : (sellerName || "");

  const showSentButton = isSeller && !sentConfirmed;
  const showReceivedButton = isBuyer && sentConfirmed && !receivedConfirmed;
  const canRate = completed && (isBuyer || isSeller);
  const isParticipant = isBuyer || isSeller;
  const nextStepText = completed
    ? t("inbox:transactionComplete", "Item received — deal complete.")
    : !isParticipant
      ? t("inbox:awaitingOtherParty", "Waiting for both sides to confirm.")
    : showSentButton
      ? t("inbox:nextStepSellerSent", "Your order has been marked as sent. Confirm when you receive it.")
      : showReceivedButton
        ? t("inbox:nextStepBuyerReceived", "Mark as received when the item arrives.")
        : sentConfirmed
          ? t("inbox:awaitingReceived", "Waiting for the buyer to confirm receipt.")
          : t("inbox:awaitingSent", "Waiting for the seller to mark as sent.");

  const formatAmount = (amt) => {
    if (amt == null || amt === "") return null;
    const num = Number(amt);
    return Number.isFinite(num) ? `${num.toLocaleString()} RWF` : `${amt} RWF`;
  };

  useEffect(() => {
    let mounted = true;
    if (!offerId) {
      setOfferDoc(null);
      return () => { mounted = false; };
    }

    (async () => {
      try {
        const offer = await offersService.getOfferById(offerId);
        if (!mounted) return;
        if (offer) setOfferDoc(offer);
      } catch (err) {
        errorDev("Operation failed:", err);
        if (mounted) setOfferDoc(null);
      }
    })();

    return () => { mounted = false; };
  }, [offerId]);

  useEffect(() => {
    if (!offerId || !chatId) return;

    const unsub = chatService.subscribeChatMeta(
      chatId,
      (chat) => {
        if (!chat) return;
        const dc = chat?.dealConfirmations?.[offerId] || {};
        const nextStatus = String(dc.status || dealStatus || "accepted").toLowerCase();
        const nextSent = Boolean(dc.sentAt);
        const nextReceived = Boolean(dc.receivedAt);
        setDealStatus((prev) => (prev === nextStatus ? prev : nextStatus));
        setSentConfirmed((prev) => (prev === nextSent ? prev : nextSent));
        setReceivedConfirmed((prev) => (prev === nextReceived ? prev : nextReceived));
      },
      () => {}
    );

    return () => unsub && unsub();
  }, [offerId, chatId]);

  const handleConfirmStep = async (action) => {
    if (!offerId) {
      setError(t("inbox:offerNotFound", "Offer not found."));
      return;
    }

    setProcessing(true);
    setError("");

    try {
      const updated = await offersService.confirmOfferDeliveryStepInChat({
        offerId,
        chatId,
        action,
        actorId: currentUser?.uid,
      });

      if (action === 'sent') setSentConfirmed(true);
      if (action === 'received') setReceivedConfirmed(true);
      if (updated?.completed) setDealStatus('completed');

      if (action === 'sent' && isSeller) {
        setRatePrompt(t("inbox:sellerSentRatePrompt", "Your order has been marked as sent. Rate your experience with the buyer once complete."));
        setShowRate(true);
      }

      if (action === 'received' && isBuyer) {
        setRatePrompt(t("inbox:buyerReceivedRatePrompt", "Item received. Rate your experience with the seller."));
        setShowRate(true);
      }

      if (onPaymentSuccess) {
        onPaymentSuccess({
          offerId,
          amount,
          status: updated?.completed ? 'completed' : 'accepted',
          action,
          changed: updated?.changed !== false,
        });
      }
    } catch (err) {
      setError(err?.message || t("inbox:deliveryConfirmFailed", "Could not confirm delivery step."));
    } finally {
      setProcessing(false);
    }
  };


  return (
    <div className={`flex ${align === "right" ? "justify-end" : "justify-start"} mb-2 px-2 sm:px-0`}>
      {/* Soft inline banner - no card, no heavy border, no shadow, no rounded-2xl */}
      <div
        className={`w-full max-w-[min(92vw,26rem)] sm:max-w-sm px-3 py-2.5 ${completed
          ? "bg-green-50 dark:bg-green-950/10 border-t border-t-green-200 dark:border-t-green-900/20"
          : "bg-card dark:bg-card border-t border-border"}
        `}
      >
        {/* Header: status + amount inline */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0">
            <span className="text-[10px] font-medium text-muted-foreground leading-tight">
              {t("inbox:orderStatus", "Order status")}
            </span>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span className={`font-bold text-lg leading-none ${completed ? "text-green-700 dark:text-green-300" : "text-foreground"}`}>
                {formatAmount(amount) || t("inbox:negotiatedAmount", "Offer amount")}
              </span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${completed
                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
              }`}>
                {completed ? t("inbox:done", "Done") : t("inbox:inProgress", "In progress")}
              </span>
            </div>
          </div>
        </div>

        {/* Product title inline, subtle */}
        {productTitle && (
          <p className="text-sm leading-snug mb-1.5 text-foreground truncate">{productTitle}</p>
        )}

        {/* Message text */}
        {msg.text && (
          <p className="text-sm leading-relaxed mb-2 text-foreground">{msg.text}</p>
        )}

        {/* Delivery milestones as simple inline steps */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">{t("inbox:sellerMarksSent", "Seller marks sent")}</span>
            <span className={`font-semibold px-1.5 py-0.5 rounded ${sentConfirmed ? "bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300" : "text-muted-foreground"}`}>
              {sentConfirmed ? t("inbox:done", "Done") : t("inbox:pending", "Pending")}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">{t("inbox:buyerMarksReceived", "Buyer confirms received")}</span>
            <span className={`font-semibold px-1.5 py-0.5 rounded ${receivedConfirmed ? "bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300" : "text-muted-foreground"}`}>
              {receivedConfirmed ? t("inbox:done", "Done") : t("inbox:pending", "Pending")}
            </span>
          </div>
        </div>

        {!completed && (
          <p className="mt-1.5 text-[10px] leading-relaxed text-muted-foreground font-medium">
            {nextStepText}
          </p>
        )}

        {ratePrompt && (
          <div className="mt-2 p-1.5 rounded bg-amber-50 dark:bg-amber-950/10 border border-amber-100 dark:border-amber-900/20">
            <p className="text-xs leading-relaxed font-semibold text-amber-800 dark:text-amber-200">{ratePrompt}</p>
          </div>
        )}

        {error && (
          <div className="mt-2 p-1.5 rounded bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20">
            <p className="text-xs leading-relaxed text-red-700 dark:text-red-300 font-medium">{error}</p>
          </div>
        )}

        {completed && (
          <div className="mt-2 p-1.5 rounded bg-green-50 dark:bg-green-950/10 border border-green-100 dark:border-green-900/20">
            <p className="text-xs font-bold text-green-700 dark:text-green-300">{t("inbox:transactionComplete", "Item received — deal complete.")}</p>
          </div>
        )}

        {showSentButton && (
          <button
            onClick={() => handleConfirmStep('sent')}
            disabled={processing}
            className={`w-full mt-2 py-2.5 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
              processing
                ? "bg-gray-300 text-gray-600 cursor-not-allowed dark:bg-neutral-700 dark:text-gray-500"
                : "bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow-md active:scale-95"
            }`}
          >
            {processing ? (
              <>
                <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" />
                {t("common:loading", "Loading...")}
              </>
            ) : t("inbox:markSent", "Mark as sent")}
          </button>
        )}

        {showReceivedButton && (
          <button
            onClick={() => handleConfirmStep('received')}
            disabled={processing}
            className={`w-full mt-2 py-2.5 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
              processing
                ? "bg-gray-300 text-gray-600 cursor-not-allowed dark:bg-neutral-700 dark:text-gray-500"
                : "bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md active:scale-95"
            }`}
          >
            {processing ? t("common:loading", "Loading...") : t("inbox:confirmReceived", "Mark as received")}
          </button>
        )}

        {canRate && (
          <button
            type="button"
            onClick={() => setShowRate(true)}
            className="mt-2 w-full py-2 rounded-lg border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
          >
            {t("inbox:rateTransaction", "Rate this transaction")}
          </button>
        )}
      </div>

      <DealRatingModal
        open={showRate}
        onClose={() => setShowRate(false)}
        chatId={chatId}
        defaultTargetLabel={ratingTargetLabel}
        offer={{
          id: offerId,
          buyerId,
          sellerId,
          productId: msg?.extra?.productId || offerDoc?.productId,
          dealStatus: completed ? 'completed' : dealStatus,
        }}
        currentUserId={currentUser?.uid}
      />
    </div>
  );
}
